/**
 * Centralized utility for service category map pin icons
 * These are used for category-based map markers in the Services marketplace
 * 
 * Pattern follows jobCategoryIcons.ts and partsTypeIcons.ts structure
 */

import helicopterSilhouetteIcon from '@/assets/helicopter-silhouette-icon.png';

// Lucide icon SVG data URLs for map markers (black strokes on transparent background)
// These are inline SVGs converted to data URLs for canvas rendering

const createLucideSvgDataUrl = (svgPath: string, viewBox = '0 0 24 24'): string => {
  // Generate at 256x256 for high-quality downscaling to ~52px display size
  const svg = `<svg xmlns="http://www.w3.org/2000/svg" viewBox="${viewBox}" width="256" height="256" fill="none" stroke="black" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">${svgPath}</svg>`;
  return `data:image/svg+xml,${encodeURIComponent(svg)}`;
};

// Lucide icon paths for each service category
const LUCIDE_PATHS = {
  // Wrench - mro
  wrench: '<path d="M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z"/>',
  // Shield - camo
  shield: '<path d="M20 13c0 5-3.5 7.5-8 8.97C7.5 20.5 4 18 4 13V6a2 2 0 0 1 2-2c2 0 4.5-1.2 6-3 1.5 1.8 4 3 6 3a2 2 0 0 1 2 2z"/>',
  // Cog - part21
  cog: '<path d="M12 20a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z"/><path d="M12 14a2 2 0 1 0 0-4 2 2 0 0 0 0 4Z"/><path d="M12 2v2"/><path d="M12 22v-2"/><path d="m17 20.66-1-1.73"/><path d="M11 10.27 7 3.34"/><path d="m20.66 17-1.73-1"/><path d="m3.34 7 1.73 1"/><path d="M14 12h8"/><path d="M2 12h2"/><path d="m20.66 7-1.73 1"/><path d="m3.34 17 1.73-1"/><path d="m17 3.34-1 1.73"/><path d="m11 13.73-4 6.93"/>',
  // GraduationCap - training
  graduationCap: '<path d="M21.42 10.922a1 1 0 0 0-.019-1.838L12.83 5.18a2 2 0 0 0-1.66 0L2.6 9.08a1 1 0 0 0 0 1.832l8.57 3.908a2 2 0 0 0 1.66 0z"/><path d="M22 10v6"/><path d="M6 12.5V16a6 3 0 0 0 12 0v-3.5"/>',
  // Settings2 - engineering
  settings2: '<path d="M20 7h-9"/><path d="M14 17H5"/><circle cx="17" cy="17" r="3"/><circle cx="7" cy="7" r="3"/>',
  // Truck - ground_ops
  truck: '<path d="M14 18V6a2 2 0 0 0-2-2H4a2 2 0 0 0-2 2v11a1 1 0 0 0 1 1h2"/><path d="M15 18h2"/><path d="M19 18h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.624l-3.48-4.35A1 1 0 0 0 17.52 8H14"/><circle cx="17" cy="18" r="2"/><circle cx="7" cy="18" r="2"/>',
  // Building - fbo_heliport
  building: '<rect width="16" height="20" x="4" y="2" rx="2" ry="2"/><path d="M9 22v-4h6v4"/><path d="M8 6h.01"/><path d="M16 6h.01"/><path d="M12 6h.01"/><path d="M12 10h.01"/><path d="M12 14h.01"/><path d="M16 10h.01"/><path d="M16 14h.01"/><path d="M8 10h.01"/><path d="M8 14h.01"/>',
  // Briefcase - management
  briefcase: '<path d="M16 20V4a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/><rect width="20" height="14" x="2" y="6" rx="2"/>',
  // Radar - uas (drone/UAS services)
  radar: '<path d="M19.07 4.93A10 10 0 0 0 6.99 3.34"/><path d="M4 6h.01"/><path d="M2.29 9.62A10 10 0 1 0 21.31 8.35"/><path d="M16.24 7.76A6 6 0 1 0 8.23 16.67"/><path d="M12 18h.01"/><circle cx="12" cy="12" r="2"/><path d="m13.41 10.59 5.66-5.66"/>',
};

// Map of service categories to their marker icon data URLs
export type ServiceCategory = 'mro' | 'camo' | 'part21' | 'training' | 'operator' | 'engineering' | 'ground_ops' | 'fbo_heliport' | 'management' | 'uas';

export const SERVICE_CATEGORY_MARKER_ICONS: Record<ServiceCategory, string> = {
  mro: createLucideSvgDataUrl(LUCIDE_PATHS.wrench),
  camo: createLucideSvgDataUrl(LUCIDE_PATHS.shield),
  part21: createLucideSvgDataUrl(LUCIDE_PATHS.cog),
  training: createLucideSvgDataUrl(LUCIDE_PATHS.graduationCap),
  operator: helicopterSilhouetteIcon, // Uses helicopter PNG for operator services
  engineering: createLucideSvgDataUrl(LUCIDE_PATHS.settings2),
  ground_ops: createLucideSvgDataUrl(LUCIDE_PATHS.truck),
  fbo_heliport: createLucideSvgDataUrl(LUCIDE_PATHS.building),
  management: createLucideSvgDataUrl(LUCIDE_PATHS.briefcase),
  uas: createLucideSvgDataUrl(LUCIDE_PATHS.radar),
};

/**
 * Get the marker icon for a specific service category
 * Use this for Google Maps markers
 */
export const getServiceCategoryMarkerIcon = (category?: string): string => {
  if (!category) return SERVICE_CATEGORY_MARKER_ICONS.management; // Default to briefcase
  return SERVICE_CATEGORY_MARKER_ICONS[category as ServiceCategory] || SERVICE_CATEGORY_MARKER_ICONS.management;
};

/**
 * Configuration for service category display including labels and colors
 * Note: This complements SERVICE_CATEGORY_CONFIG in serviceTypeIcons.ts
 */
export const SERVICE_CATEGORY_MARKER_CONFIG: Record<ServiceCategory, {
  label: string;
  shortLabel: string;
  color: string;
  bgColor: string;
}> = {
  mro: {
    label: 'MRO / Part-145',
    shortLabel: 'MRO',
    color: 'text-blue-600',
    bgColor: 'bg-blue-100',
  },
  camo: {
    label: 'CAMO',
    shortLabel: 'CAMO',
    color: 'text-purple-600',
    bgColor: 'bg-purple-100',
  },
  part21: {
    label: 'Part-21 DOA/POA',
    shortLabel: 'Part-21',
    color: 'text-indigo-600',
    bgColor: 'bg-indigo-100',
  },
  training: {
    label: 'Training & Education',
    shortLabel: 'Training',
    color: 'text-green-600',
    bgColor: 'bg-green-100',
  },
  operator: {
    label: 'Operator Services',
    shortLabel: 'Operator',
    color: 'text-sky-600',
    bgColor: 'bg-sky-100',
  },
  engineering: {
    label: 'Engineering & Technical',
    shortLabel: 'Engineering',
    color: 'text-slate-600',
    bgColor: 'bg-slate-100',
  },
  ground_ops: {
    label: 'Ground Operations',
    shortLabel: 'Ground Ops',
    color: 'text-orange-600',
    bgColor: 'bg-orange-100',
  },
  fbo_heliport: {
    label: 'FBO / Heliport',
    shortLabel: 'FBO',
    color: 'text-rose-600',
    bgColor: 'bg-rose-100',
  },
  management: {
    label: 'Management Services',
    shortLabel: 'Management',
    color: 'text-amber-600',
    bgColor: 'bg-amber-100',
  },
  uas: {
    label: 'Drone / UAS Services',
    shortLabel: 'UAS',
    color: 'text-teal-600',
    bgColor: 'bg-teal-100',
  },
};

/**
 * Badge styling config for service categories used in MarketplaceListingCard
 * Single source of truth for category → badge color + label mapping
 */
export const SERVICE_CATEGORY_BADGE_CONFIG: Record<string, { label: string; badgeColor: string; color: string; bgColor: string }> = {
  mro: { label: 'MRO / Part-145', badgeColor: 'bg-blue-500 text-white', color: 'text-blue-700 dark:text-blue-300', bgColor: 'bg-blue-100 dark:bg-blue-900/30' },
  camo: { label: 'CAMO', badgeColor: 'bg-purple-500 text-white', color: 'text-purple-700 dark:text-purple-300', bgColor: 'bg-purple-100 dark:bg-purple-900/30' },
  part21: { label: 'Part-21', badgeColor: 'bg-orange-500 text-white', color: 'text-orange-700 dark:text-orange-300', bgColor: 'bg-orange-100 dark:bg-orange-900/30' },
  training: { label: 'Training', badgeColor: 'bg-green-500 text-white', color: 'text-green-700 dark:text-green-300', bgColor: 'bg-green-100 dark:bg-green-900/30' },
  operator: { label: 'Operator', badgeColor: 'bg-sky-500 text-white', color: 'text-sky-700 dark:text-sky-300', bgColor: 'bg-sky-100 dark:bg-sky-900/30' },
  engineering: { label: 'Engineering', badgeColor: 'bg-slate-500 text-white', color: 'text-slate-700 dark:text-slate-300', bgColor: 'bg-slate-100 dark:bg-slate-900/30' },
  ground_ops: { label: 'Ground Ops', badgeColor: 'bg-amber-500 text-white', color: 'text-amber-700 dark:text-amber-300', bgColor: 'bg-amber-100 dark:bg-amber-900/30' },
  fbo_heliport: { label: 'FBO / Heliport', badgeColor: 'bg-indigo-500 text-white', color: 'text-indigo-700 dark:text-indigo-300', bgColor: 'bg-indigo-100 dark:bg-indigo-900/30' },
  management: { label: 'Management', badgeColor: 'bg-rose-500 text-white', color: 'text-rose-700 dark:text-rose-300', bgColor: 'bg-rose-100 dark:bg-rose-900/30' },
  uas: { label: 'Drone / UAS', badgeColor: 'bg-cyan-500 text-white', color: 'text-cyan-700 dark:text-cyan-300', bgColor: 'bg-cyan-100 dark:bg-cyan-900/30' },
};

/**
 * Get badge config for a service category, with fallback
 */
export const getServiceCategoryBadgeConfig = (category?: string) => {
  if (!category) return { label: 'Service', badgeColor: 'bg-primary text-primary-foreground', color: 'text-primary', bgColor: 'bg-primary/10' };
  return SERVICE_CATEGORY_BADGE_CONFIG[category] || { label: category, badgeColor: 'bg-primary text-primary-foreground', color: 'text-primary', bgColor: 'bg-primary/10' };
};

/**
 * Get category label from category value
 */
export const getServiceCategoryLabel = (category?: string): string => {
  if (!category) return 'Service';
  return SERVICE_CATEGORY_MARKER_CONFIG[category as ServiceCategory]?.label || category;
};

/**
 * Get category short label from category value
 */
export const getServiceCategoryShortLabel = (category?: string): string => {
  if (!category) return 'Service';
  return SERVICE_CATEGORY_MARKER_CONFIG[category as ServiceCategory]?.shortLabel || category;
};
